import { type ReactNode, useMemo, useState } from "react";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { DocumentPreview } from "./DocumentPreview";

interface FullScreenDocumentPreviewProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  title?: string;
  sidePanel?: ReactNode;
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

function isImageUrl(url: string): boolean {
  const ext = url.toLowerCase().split("?")[0].split(".").pop() ?? "";
  return IMAGE_EXTENSIONS.includes(ext);
}

const SIDE_PANEL_WIDTH = 360;

export function FullScreenDocumentPreview({
  open,
  onClose,
  fileUrl,
  title = "Document Preview",
  sidePanel,
}: FullScreenDocumentPreviewProps) {
  const showImage = useMemo(() => isImageUrl(fileUrl), [fileUrl]);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  return (
    <SafeDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          width: "95vw",
          height: "95vh",
          maxWidth: "95vw",
          maxHeight: "95vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="span" noWrap>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {sidePanel && (
            <Tooltip title={sidePanelOpen ? "Hide comments" : "Show comments"}>
              <IconButton
                aria-label="toggle comments"
                onClick={() => setSidePanelOpen((prev) => !prev)}
                color={sidePanelOpen ? "primary" : "default"}
                size="small"
              >
                <ChatBubbleOutline />
              </IconButton>
            </Tooltip>
          )}
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
          {showImage ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
                backgroundColor: "#f5f5f5",
              }}
            >
              <img
                src={fileUrl}
                alt="Document preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <DocumentPreview fileUrl={fileUrl} />
          )}
        </Box>
        {sidePanel && sidePanelOpen && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box
              sx={{
                width: SIDE_PANEL_WIDTH,
                minWidth: SIDE_PANEL_WIDTH,
                height: "100%",
                overflowY: "auto",
                p: 2,
              }}
            >
              {sidePanel}
            </Box>
          </>
        )}
      </DialogContent>
    </SafeDialog>
  );
}
